import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import InteractiveHoverButton from "./ui/interactive-hover-button";
import { triggerConsultaPopup } from "./ConsultaPopup";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Sobre", href: "/sobre" },
    { name: "Tratamentos", href: "/tratamentos" },
    { name: "Avaliações", href: "/avaliacoes" },
    { name: "Teleconsulta", href: "/teleconsulta" },
    { name: "FAQ", href: "/duvidas-frequentes" },
    { name: "Blog", href: "/blog" },
  ];

  const treatmentsList = [
    {
      name: "Dermatologia Clínica (Doenças de pele)",
      href: "/tratamentos/dermatologia-clinica",
      image: "https://assets.cdn.filesafe.space/rc8Fx2cZBlMBT1xVdV7Z/media/6a0df5a364c77d18434078c3.webp",
    },
    {
      name: "Tratamento de Acnes e Cicatrizes",
      href: "/tratamentos/acnes-e-cicatrizes",
      image: "https://assets.cdn.filesafe.space/rc8Fx2cZBlMBT1xVdV7Z/media/6a0df5a6e05851175c1a3ae9.webp",
    },
    {
      name: "Tricologia (Doenças no couro cabeludo)",
      href: "/tratamentos/tricologia-cabelos",
      image: "https://assets.cdn.filesafe.space/rc8Fx2cZBlMBT1xVdV7Z/media/6a0df5a8ce0ec8e60c246875.webp",
    },
    {
      name: "Onicologia (Doenças nas unhas)",
      href: "/tratamentos/onicologia-unhas",
      image: "https://assets.cdn.filesafe.space/rc8Fx2cZBlMBT1xVdV7Z/media/6a0df5aad3dfb498edc0bf48.webp",
    }
  ];

  const handleLinkClick = (e: React.MouseEvent, href: string) => {
    setIsOpen(false);
    if (href.startsWith("/#") && location.pathname === "/") {
      e.preventDefault();
      const id = href.replace("/#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else if (href === location.pathname) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/90 backdrop-blur-md shadow-sm py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="container-custom flex items-center justify-between">
        <Link to="/" onClick={handleLogoClick} className="flex flex-col leading-tight group">
          <span className="text-xl md:text-2xl font-serif font-bold text-foreground">
            Dra. Adriana <span className="text-stone-600 font-medium">Rodrigues</span>
          </span>
          <span className="text-[10px] md:text-xs uppercase tracking-[0.3em] font-bold group-hover:text-primary transition-colors text-primary">
            Dermatologista
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-10">
          <div className="flex items-center gap-8">
            {navLinks.map((link) => {
              if (link.name === "Tratamentos") {
                return (
                  <div key={link.name} className="relative group/menu">
                    <a
                      href={link.href}
                      className={`text-[15px] transition-colors relative py-1 flex items-center gap-1 ${
                        location.pathname.includes("/tratamentos") || location.pathname.includes("/onicologia-unhas") || location.pathname.includes("/dermatologia-clinica")
                          ? "text-primary after:absolute after:bottom-0 after:left-0 after:w-full after:h-[1px] after:bg-primary"
                          : "text-foreground/80 hover:text-primary after:absolute after:bottom-0 after:left-0 after:w-0 hover:after:w-full after:h-[1px] after:bg-primary after:transition-all after:duration-300"
                      }`}
                      onClick={(e) => handleLinkClick(e, link.href)}
                    >
                      {link.name}
                    </a>
                    
                    {/* Mega Menu Dropdown */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-[30px] opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all duration-500 ease-out z-50 translate-y-4 group-hover/menu:translate-y-0">
                      <div className="bg-white rounded-2xl shadow-xl border border-black/5 p-6 w-[800px] grid grid-cols-4 gap-4 relative before:absolute before:-top-2 before:left-1/2 before:-translate-x-1/2 before:w-4 before:h-4 before:bg-white before:rotate-45 before:border-l before:border-t before:border-black/5">
                        {treatmentsList.map((treatment, index) => (
                          <Link 
                            key={treatment.name} 
                            to={treatment.href}
                            className="group/item flex flex-col gap-3 opacity-0 translate-y-4 group-hover/menu:opacity-100 group-hover/menu:translate-y-0 transition-all duration-700 ease-out"
                            style={{ transitionDelay: `${150 + index * 150}ms` }}
                          >
                            <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden">
                              <img 
                                src={treatment.image} 
                                alt={treatment.name}
                                className={`w-full h-full object-cover transition-transform duration-500 group-hover/item:scale-110 ${treatment.name.includes("Onicologia") ? "object-[20%_center]" : ""}`}
                              />
                              <div className="absolute inset-0 bg-black/20 group-hover/item:bg-black/10 transition-colors" />
                            </div>
                            <span className="text-sm font-medium text-foreground/80 group-hover/item:text-primary transition-colors text-center leading-tight">
                              {treatment.name}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              const isActive = location.pathname === link.href || (link.href.includes('#') && location.hash === link.href.replace('/', ''));
              return (
                <a
                  key={link.name}
                  href={link.href}
                  className={`text-[15px] transition-colors relative py-1 ${
                    isActive
                      ? "text-primary after:absolute after:bottom-0 after:left-0 after:w-full after:h-[1px] after:bg-primary"
                      : "text-foreground/80 hover:text-primary after:absolute after:bottom-0 after:left-0 after:w-0 hover:after:w-full after:h-[1px] after:bg-primary after:transition-all after:duration-300"
                  }`}
                  onClick={(e) => handleLinkClick(e, link.href)}
                >
                  {link.name}
                </a>
              );
            })}
          </div>
          <InteractiveHoverButton 
            classes="bg-primary hover:bg-accent text-[12px] text-white font-bold transition-all duration-300 shadow-lg shadow-primary/20 py-3 px-8"
            onClick={triggerConsultaPopup}
            hoverContent={
              <>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <span>Abrir WhatsApp</span>
              </>
            }
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Agendar consulta
          </InteractiveHoverButton>
        </div>

        {/* Mobile Toggle */}
        <button aria-label="Menu" className="lg:hidden text-foreground p-2" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-b border-border p-8 flex flex-col gap-6 animate-in slide-in-from-top duration-300 shadow-2xl max-h-[80vh] overflow-y-auto">
          {navLinks.map((link) => {
            const isActive = location.pathname === link.href || (link.href.includes('#') && location.hash === link.href.replace('/', ''));
            return (
              <a
                key={link.name}
                href={link.href}
                className={`text-[15px] py-2 border-b border-black/5 transition-colors relative w-fit ${
                  isActive
                    ? "text-primary after:absolute after:bottom-2 after:left-0 after:w-full after:h-[1px] after:bg-primary"
                    : "text-foreground/80 hover:text-primary"
                }`}
                onClick={(e) => handleLinkClick(e, link.href)}
              >
                {link.name}
              </a>
            );
          })}
          <InteractiveHoverButton 
            classes="w-full bg-primary hover:bg-accent text-white py-4 text-[12px] font-bold mt-4"
            onClick={triggerConsultaPopup}
            hoverContent={
              <>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <span>Abrir</span>
              </>
            }
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Agendar consulta
          </InteractiveHoverButton>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
